/**
 * Project Store (Zustand)
 * Manages project list, CRUD, and current project context
 */

import { create } from 'zustand';
import * as projectApi from '../services/projectApi';
import type { Project, ProjectWithTeams, CreateProjectData } from '../services/projectApi';

export type { Project, ProjectWithTeams, CreateProjectData };

interface ProjectStore {
  projects: ProjectWithTeams[];
  unassignedTeams: any[];
  loading: boolean;
  error: string | null;

  loadProjects: () => Promise<void>;
  createProject: (data: CreateProjectData) => Promise<Project>;
  updateProject: (id: number, data: Partial<CreateProjectData>) => Promise<Project>;
  deleteProject: (id: number) => Promise<void>;
  shareProject: (projectId: number, email: string, role: string) => Promise<void>;
  revokeAccess: (projectId: number, email: string) => Promise<void>;
  updateTeamInProject: (teamId: number, updates: Record<string, any>) => void;
}

export const useProjectStore = create<ProjectStore>()((set) => ({
  projects: [],
  unassignedTeams: [],
  loading: false,
  error: null,

  loadProjects: async () => {
    set({ loading: true, error: null });
    try {
      const data = await projectApi.listProjects();
      set({ projects: data.projects, unassignedTeams: data.unassigned_teams, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      console.error('Failed to load projects:', error);
    }
  },

  createProject: async (data: CreateProjectData) => {
    const project = await projectApi.createProject(data);
    set(state => ({ projects: [{ ...project, teams: [], team_count: 0 } as ProjectWithTeams, ...state.projects] }));
    return project;
  },

  updateProject: async (id: number, data: Partial<CreateProjectData>) => {
    const updated = await projectApi.updateProject(id, data);
    set(state => ({
      projects: state.projects.map(p => p.id === id ? { ...p, ...updated } : p),
    }));
    return updated;
  },

  deleteProject: async (id: number) => {
    await projectApi.deleteProject(id);
    set(state => ({
      projects: state.projects.filter(p => p.id !== id),
    }));
  },

  shareProject: async (projectId: number, email: string, role: string) => {
    await projectApi.shareProject(projectId, email, role);
  },

  revokeAccess: async (projectId: number, email: string) => {
    await projectApi.revokeProjectAccess(projectId, email);
  },

  updateTeamInProject: (teamId: number, updates: Record<string, any>) => {
    set(state => ({
      projects: state.projects.map(project => ({
        ...project,
        teams: project.teams.map((team: any) =>
          team.id === teamId ? { ...team, ...updates } : team
        ),
      })),
    }));
  },
}));
